import {
  Bell, Users, User, Network, Sparkles, IdCard, Mail, MailOpen, Info, ArrowLeft,
  Tag, Tags, Settings, Send, Code, Zap, Ban, TriangleAlert, Pause, PauseCircle,
  Play, CheckCircle, Pencil, Trash2, Eye, Plus, SlidersHorizontal, Inbox,
  Calendar, List, History, Clock, Cpu, Wrench, Bot, Server, RefreshCw, Search,
  X, XCircle, FileText, Folder, Database, KeyRound, LayoutGrid, UserCog, Shield, Copy,
  Download, Check, ChevronLeft, ChevronRight, HelpCircle,
  Building2, MapPin, Globe, Share2, MessagesSquare, MessageSquare, LineChart,
  CircleAlert, Briefcase, SquareCheck, ArrowUpRight, Filter, Save, Upload,
  ExternalLink, CirclePlus, UserPlus, BookOpen, Home,
  Menu, ChevronDown, LogOut,
  EllipsisVertical, EyeOff, Table, Square, ArrowUpNarrowWide, ArrowDownWideNarrow,
  Handshake, Usb,
  MessageSquareMore, MessageSquareText, MessageSquareCheck, SquareUserRound,
  ListChecks, CalendarClock, ClipboardClock, ServerCog,
  BellRing, Repeat2, FileCog, FileChartColumn,
  ListPlus, BookUser, ContactRound, Settings2,
  ShieldUser, Blocks, UserRoundCog, Link2, UserCheck, UsersRound,
  type LucideIcon,
} from 'lucide-react';

import projectsPng from '../../assets/icons/projects.png';
import userAccessPng from '../../assets/icons/user-access.png';

/**
 * Bitmap icons that have no Lucide equivalent, supplied as artwork.
 *
 * Drawn as a CSS mask rather than an <img>, so the glyph takes `currentColor`
 * like every other icon: it turns indigo when its menu item is active and stays
 * legible in dark mode. A plain <img> of a black PNG would do neither — it
 * would sit black on the dark sidebar and ignore the active state.
 */
const ASSETS = {
  projects: projectsPng,
  'user-access': userAccessPng,
} as const;

/**
 * The app's icon set — one registry, so an icon is chosen by MEANING, not by
 * remembering a class string. Swapping what "network" looks like is one edit
 * here rather than a hunt through the screens, and "sitemap in one place,
 * share-alt in another" cannot happen.
 *
 * Names are kept close to the old PrimeIcons vocabulary so the migration reads
 * as a rename. Default stroke is 1.75 — a touch finer than Lucide's 2, for a
 * more premium line.
 */
const REGISTRY = {
  bell: Bell,
  users: Users,
  user: User,
  network: Network,          // resolution chains, org / sitemap
  sparkles: Sparkles,
  'id-card': IdCard,
  mail: Mail,
  // The inbox's read/unread flip. PrimeIcons has `pi-envelope` but no
  // `pi-envelope-open`, so the unread row's button rendered an empty circle.
  'mail-open': MailOpen,
  info: Info,
  'arrow-left': ArrowLeft,
  tag: Tag,
  tags: Tags,
  settings: Settings,        // cog
  send: Send,
  code: Code,
  zap: Zap,                  // actions / bolt
  ban: Ban,
  'alert-triangle': TriangleAlert,
  pause: Pause,
  'pause-circle': PauseCircle,
  play: Play,
  'check-circle': CheckCircle,
  pencil: Pencil,
  trash: Trash2,
  eye: Eye,
  plus: Plus,
  sliders: SlidersHorizontal,
  inbox: Inbox,
  calendar: Calendar,
  list: List,
  history: History,
  clock: Clock,
  cpu: Cpu,
  wrench: Wrench,
  bot: Bot,
  server: Server,
  refresh: RefreshCw,
  search: Search,
  x: X,
  'x-circle': XCircle,
  'file-text': FileText,
  folder: Folder,
  database: Database,
  key: KeyRound,
  'layout-grid': LayoutGrid,
  'user-cog': UserCog,
  shield: Shield,
  copy: Copy,
  download: Download,
  check: Check,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  // Navigation / chrome
  building: Building2,
  'map-pin': MapPin,
  globe: Globe,
  share: Share2,
  comments: MessagesSquare,
  comment: MessageSquare,
  'chart-line': LineChart,
  'alert-circle': CircleAlert,
  briefcase: Briefcase,
  'check-square': SquareCheck,
  'arrow-up-right': ArrowUpRight,
  filter: Filter,
  save: Save,
  upload: Upload,
  'external-link': ExternalLink,
  'plus-circle': CirclePlus,
  'user-plus': UserPlus,
  book: BookOpen,
  home: Home,
  menu: Menu,
  'chevron-down': ChevronDown,
  'log-out': LogOut,
  // Data-table controls
  'more-vertical': EllipsisVertical,
  'eye-off': EyeOff,
  table: Table,
  square: Square,
  // Access-control vocabulary — supplied by design.
  'shield-user': ShieldUser,        // permissions
  blocks: Blocks,                   // permission sets
  'user-round-cog': UserRoundCog,   // roles
  'link-2': Link2,                  // role bindings — a binding links user to role
  'user-check': UserCheck,          // user access — a user who has been granted
  'users-round': UsersRound,        // user master — a group of people
  'sort-asc': ArrowUpNarrowWide,
  'sort-desc': ArrowDownWideNarrow,
  // Partners & Integrations. No PrimeIcons equivalent exists for any of these,
  // so they are named directly rather than reached through PI_TO_ICON.
  handshake: Handshake,        // the section: partners + integrations
  usb: Usb,                    // connection types
  // Feedback. Every stage is a speech bubble, told apart by what sits inside it,
  // so the loop reads as one conversation rather than as paperwork. Not `comments`
  // (stacked bubbles) for the section — AI > Prompts already renders that, and
  // two identical icons in one sidebar is worse than no icon.
  'message-dots': MessageSquareMore,    // the Feedback section
  // Lucide has no speech-bubble-with-person, so this is the nearest: a person
  // framed in a rounded box, which still sits with the bubbles around it.
  'user-square': SquareUserRound,       // my own feedback — me, in the box
  'message-text': MessageSquareText,    // a submission — the words that came in
  'message-check': MessageSquareCheck,  // a submission reviewed, verdict given
  // Background jobs, read as WHAT / WHEN / HOW rather than three grey cogs. The
  // section itself reuses `cpu` above — the machine doing the work; not `server`,
  // which AI Models already owns. And deliberately not `settings` or `sliders`
  // for the engine tab: App Configuration owns sliders, and two of those in one
  // sidebar is how people end up on the wrong screen.
  'list-checks': ListChecks,     // Job Master: the catalogue of what should run
  'clipboard-clock': ClipboardClock, // Scheduler: when it should run
  'calendar-clock': CalendarClock, // a schedule's next occurrences
  'server-cog': ServerCog,       // Settings: how the engine itself is configured
  // Notification Engine: one broadcast, then who it reaches and when. The
  // three reused here (`sliders`, `users`, `zap`) were each already owned by a
  // sidebar collection — App Configuration, Users and Functions — so the tabs
  // take their own rather than echo another screen.
  'bell-ring': BellRing,         // the section: a notification actually firing
  'settings-2': Settings2,       // notification preferences, not app config
  contact: ContactRound,         // one recipient — an address-book entry
  'book-user': BookUser,         // a recipient LIST — the address book itself
  // Value Sets: a list you add codes to.
  'list-plus': ListPlus,
  // Transactional Dashboard: the record of what the system DID. One file per
  // kind of record, so the four tabs read as four reports rather than four moods.
  'repeat-2': Repeat2,           // the section: everything that ran, again and again
  'file-chart': FileChartColumn, // the audit log — a report of activity
  'file-cog': FileCog,           // the background-job record
} satisfies Record<string, LucideIcon>;

export type IconName = keyof typeof REGISTRY | keyof typeof ASSETS;

/**
 * Legacy PrimeIcons class → registry name. Lets existing `icon="pi pi-*"` call
 * sites (buttons, tabs) render Lucide with no per-site edits. A class with no
 * mapping returns undefined, and the caller falls back to the PrimeIcon — so
 * nothing disappears while the map fills in.
 */
const PI_TO_ICON: Record<string, IconName> = {
  // Opt-in alias: nav entries that want supplied artwork instead of a Lucide glyph.
  'asset-projects': 'projects',
  'asset-user-access': 'user-access',
  'pi-plus': 'plus', 'pi-plus-circle': 'plus-circle', 'pi-pencil': 'pencil',
  'pi-trash': 'trash', 'pi-eye': 'eye', 'pi-check': 'check',
  'pi-check-circle': 'check-circle', 'pi-check-square': 'check-square',
  'pi-times': 'x', 'pi-times-circle': 'x-circle', 'pi-search': 'search',
  'pi-filter': 'filter', 'pi-refresh': 'refresh', 'pi-sync': 'refresh',
  'pi-arrow-left': 'arrow-left', 'pi-arrow-up-right': 'arrow-up-right',
  'pi-angle-left': 'chevron-left', 'pi-angle-right': 'chevron-right',
  'pi-bell': 'bell', 'pi-users': 'users', 'pi-user': 'user',
  'pi-user-edit': 'user-cog', 'pi-user-plus': 'user-plus', 'pi-inbox': 'inbox',
  'pi-sliders-h': 'sliders', 'pi-tags': 'tags', 'pi-tag': 'tag', 'pi-list': 'list',
  'pi-bolt': 'zap', 'pi-calendar': 'calendar', 'pi-play': 'play',
  'pi-sitemap': 'network', 'pi-share-alt': 'share', 'pi-ban': 'ban',
  'pi-cog': 'settings', 'pi-send': 'send', 'pi-code': 'code', 'pi-info-circle': 'info',
  'pi-exclamation-triangle': 'alert-triangle', 'pi-exclamation-circle': 'alert-circle',
  'pi-history': 'history', 'pi-clock': 'clock', 'pi-database': 'database',
  'pi-server': 'server', 'pi-key': 'key', 'pi-shield': 'shield',
  'pi-th-large': 'layout-grid', 'pi-id-card': 'id-card', 'pi-android': 'bot',
  'pi-microchip': 'cpu', 'pi-wrench': 'wrench', 'pi-copy': 'copy',
  'pi-download': 'download', 'pi-upload': 'upload', 'pi-save': 'save',
  'pi-external-link': 'external-link', 'pi-building': 'building',
  'pi-map-marker': 'map-pin', 'pi-globe': 'globe', 'pi-comments': 'comments',
  'pi-comment': 'comment', 'pi-chart-line': 'chart-line', 'pi-briefcase': 'briefcase',
  'pi-file-edit': 'file-text', 'pi-book': 'book', 'pi-home': 'home', 'pi-folder': 'folder',
  'pi-bars': 'menu', 'pi-angle-down': 'chevron-down', 'pi-sign-out': 'log-out',
  'pi-ellipsis-v': 'more-vertical', 'pi-eye-slash': 'eye-off', 'pi-table': 'table',
  'pi-stop': 'square', 'pi-filter-fill': 'filter',
  'pi-sort-amount-up-alt': 'sort-asc', 'pi-sort-amount-down': 'sort-desc',
};

/** Resolve a PrimeIcons class string (e.g. "pi pi-plus") to a registry name.
 *  `pi-spin` (the spinner animation modifier) is ignored so loaders stay on
 *  PrimeIcons and keep spinning. */
export const piIconName = (cls?: string): IconName | undefined => {
  if (!cls) return undefined;
  for (const token of cls.split(/\s+/)) {
    if (token === 'pi' || token === 'pi-spin' || token === 'pi-spinner') continue;
    const name = PI_TO_ICON[token];
    if (name) return name;
    // A registry name used directly, for an icon with no PrimeIcons ancestor to
    // migrate from (handshake, usb, …). Same call sites, same `icon` string
    // fields — they just name the icon instead of the class it replaced.
    if (token in REGISTRY) return token as IconName;
  }
  return undefined;
};

interface IconProps {
  name: IconName;
  /** Pixel size (width & height). Default 16. */
  size?: number;
  /** Extra classes — colour, margins, etc. */
  className?: string;
  /** Override the default 1.75 stroke. */
  strokeWidth?: number;
}

export const Icon = ({ name, size = 16, className, strokeWidth = 1.75 }: IconProps) => {
  const asset = (ASSETS as Record<string, string | undefined>)[name];
  if (asset) {
    return (
      <span
        aria-hidden
        className={className}
        style={{
          display: 'inline-block',
          width: size,
          height: size,
          backgroundColor: 'currentColor',
          WebkitMaskImage: `url(${asset})`,
          maskImage: `url(${asset})`,
          WebkitMaskSize: 'contain',
          maskSize: 'contain',
          WebkitMaskRepeat: 'no-repeat',
          maskRepeat: 'no-repeat',
          WebkitMaskPosition: 'center',
          maskPosition: 'center',
        }}
      />
    );
  }
  const C = REGISTRY[name as keyof typeof REGISTRY] ?? HelpCircle;
  return <C size={size} strokeWidth={strokeWidth} className={className} aria-hidden />;
};

export default Icon;
